import { type AssetClass, CANONICAL_ASSET_CLASSES } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Menu, MenuItem, MenuPopover, MenuSection, MenuTrigger } from '@vizit/atoms';
import { sectionedMenuStyles } from '@vizit/atoms';
import { FilterLinesIcon } from '@vizit/icons';
import { cva } from '@vizit/theme';
import * as React from 'react';
import { Header, type Key, type Selection } from 'react-aria-components';

/**
 * Every valid asset-source filter value, in display order.
 */
const ASSETS_FILTER_SOURCES = ['all', 'imported', 'spark'] as const;

/**
 * Which asset sources are visible in the grid the menu filters.
 * `'all'` shows both imported and Spark (generated) assets.
 */
type AssetsFilterSource = (typeof ASSETS_FILTER_SOURCES)[number];

const SOURCE_IMPORTED_KEY = 'source:imported';
const SOURCE_SPARK_KEY = 'source:spark';
const CLASS_ALL_KEY = 'class:all';
const CLASS_KEY_PREFIX = 'class:';

const styles = {
  ...sectionedMenuStyles,
  // The MenuItem atom's disabled state pairs `text-disabled` with a
  // `bg-primary-disabled` row fill. This menu keeps only the disabled text
  // token — options without results read as plain grayed-out labels (per the
  // filter design), not filled rows.
  item: cva({ base: 'cursor-pointer data-[disabled]:bg-transparent' }),
};

interface AssetsFilterMenuProps {
  /**
   * The currently-applied source filter. `'all'` renders a check on both
   * source items.
   * @required
   * @type {AssetsFilterSource}
   */
  source: AssetsFilterSource;
  /**
   * Called with the next source filter when a source item is toggled.
   * Toggling the only visible source (or re-checking the second one) resolves
   * back to `'all'` — the menu never produces an empty source selection.
   * @required
   * @type {(source: AssetsFilterSource) => void}
   */
  onSourceChange: (source: AssetsFilterSource) => void;
  /**
   * Whether the CLASS section is rendered. Grids without a class filter (e.g.
   * the hero tab) set this false to get a source-only menu.
   * @optional
   * @type {boolean}
   * @default true
   */
  showClassFilter?: boolean;
  /**
   * The currently-applied class filter. `null` means all classes. Only
   * meaningful while `showClassFilter` is true.
   * @optional
   * @type {AssetClass | null}
   * @default null
   */
  assetClass?: AssetClass | null;
  /**
   * Called with the next class filter — `null` when "All classes" is picked or
   * the active class is toggled off. Only called while `showClassFilter` is
   * true.
   * @optional
   * @type {(assetClass: AssetClass | null) => void}
   */
  onAssetClassChange?: (assetClass: AssetClass | null) => void;
  /**
   * The classes offered in the CLASS section.
   * @optional
   * @type {readonly AssetClass[]}
   * @default CANONICAL_ASSET_CLASSES
   */
  classes?: readonly AssetClass[];
  /**
   * Sources with no matching assets, rendered disabled, unchecked, and
   * unselectable — including in the `'all'` state, where only the populated
   * source shows a check. The one exception is a source that is itself the
   * applied single-source filter, which stays enabled and checked so a stale
   * filter can always be untoggled.
   * @optional
   * @type {readonly ('imported' | 'spark')[]}
   * @default []
   */
  disabledSources?: readonly ('imported' | 'spark')[];
  /**
   * Classes with no matching assets, rendered disabled and unselectable. The
   * currently-applied class stays enabled so it can always be untoggled, and
   * "All classes" is never disabled.
   * @optional
   * @type {readonly AssetClass[]}
   * @default []
   */
  disabledClasses?: readonly AssetClass[];
  /**
   * Disables the trigger button.
   * @optional
   * @type {boolean}
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Accessible label for the trigger button and menu.
   * @optional
   * @type {string}
   * @default 'Filter assets'
   */
  ariaLabel?: string;
  /**
   * Pass-through for the trigger button's class.
   * @optional
   * @type {string}
   */
  className?: string;
  /**
   * Test id applied to the trigger button.
   * @optional
   * @type {string}
   */
  'data-testid'?: string;
}

/**
 * "Filter" popout for asset grids: a sectioned menu (same paradigm as the
 * workspace/organization switcher) filtering by SOURCE (Imported / Spark) and
 * CLASS (All classes / one canonical asset class). Grids without a class
 * filter render a source-only menu via `showClassFilter={false}`.
 *
 * Selection semantics:
 * - SOURCE behaves like a pair of checkboxes that can never both be empty:
 *   both checked = `'all'`; unchecking one filters to the other; toggling from
 *   a single-source state returns to `'all'`.
 * - CLASS behaves like a radio group with "All classes" as the reset option;
 *   re-picking the active class also resets to all.
 *
 * The menu uses `selectionMode="multiple"` so the popover stays open while the
 * user adjusts filters; the actual single-toggle semantics above are resolved
 * from the one key react-aria toggles per press.
 */
function AssetsFilterMenu(props: AssetsFilterMenuProps) {
  const {
    source,
    onSourceChange,
    showClassFilter = true,
    assetClass = null,
    onAssetClassChange,
    classes = CANONICAL_ASSET_CLASSES,
    disabledSources = [],
    disabledClasses = [],
    isDisabled,
    ariaLabel = 'Filter assets',
    className,
    'data-testid': dataTestId,
  } = props;

  const selectedKeys = React.useMemo(() => {
    const keys = new Set<Key>();
    // Under 'all', an empty source is not part of the effective filter — it
    // renders unchecked (and disabled below) rather than checked-but-inert.
    if (source === 'imported' || (source === 'all' && !disabledSources.includes('imported'))) {
      keys.add(SOURCE_IMPORTED_KEY);
    }
    if (source === 'spark' || (source === 'all' && !disabledSources.includes('spark'))) {
      keys.add(SOURCE_SPARK_KEY);
    }
    if (showClassFilter) {
      keys.add(assetClass ? `${CLASS_KEY_PREFIX}${assetClass}` : CLASS_ALL_KEY);
    }
    return keys;
  }, [source, assetClass, disabledSources, showClassFilter]);

  const handleSelectionChange = React.useCallback(
    (selection: Selection) => {
      if (selection === 'all') {
        return;
      }

      // react-aria toggles exactly one key per press in multiple-selection
      // mode, so the toggled key is the symmetric difference between the new
      // selection and the currently-applied keys.
      const toggledKey =
        [...selection].find((key) => !selectedKeys.has(key)) ?? [...selectedKeys].find((key) => !selection.has(key));

      if (toggledKey === undefined) {
        return;
      }

      const toggled = String(toggledKey);

      if (toggled === SOURCE_IMPORTED_KEY || toggled === SOURCE_SPARK_KEY) {
        const toggledSource = toggled === SOURCE_IMPORTED_KEY ? 'imported' : 'spark';
        // From a single source, any source press resolves back to 'all'
        // (either re-checking the second source or unchecking the last one,
        // which is disallowed).
        if (source !== 'all') {
          onSourceChange('all');
          return;
        }
        // From 'all', unchecking one source leaves the other — unless the
        // other has no assets, in which case the press would produce an empty
        // view and is a no-op instead.
        const remaining = toggledSource === 'imported' ? 'spark' : 'imported';
        if (!disabledSources.includes(remaining)) {
          onSourceChange(remaining);
        }
        return;
      }

      if (toggled === CLASS_ALL_KEY) {
        onAssetClassChange?.(null);
        return;
      }

      const nextClass = toggled.slice(CLASS_KEY_PREFIX.length) as AssetClass;
      onAssetClassChange?.(nextClass === assetClass ? null : nextClass);
    },
    [selectedKeys, source, assetClass, disabledSources, onSourceChange, onAssetClassChange],
  );

  return (
    <MenuTrigger>
      <Button
        aria-label={ariaLabel}
        isDisabled={isDisabled}
        level={ButtonLevel.Secondary}
        size={ButtonSize.Sm}
        className={className}
        iconStart={<FilterLinesIcon size={16} />}
        data-testid={dataTestId}
      >
        Filter
      </Button>
      <MenuPopover placement="bottom start" className={styles.popover()}>
        <Menu
          aria-label={ariaLabel}
          className={styles.menu()}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
        >
          <MenuSection className={styles.section()}>
            <Header className={styles.header()}>SOURCE</Header>
            {/* An empty source is disabled outright — including under 'all'.
                The one exception is the applied single-source filter, which
                stays enabled so it can always be untoggled. */}
            <MenuItem
              id={SOURCE_IMPORTED_KEY}
              textValue="Imported"
              className={styles.item()}
              isDisabled={disabledSources.includes('imported') && source !== 'imported'}
            >
              Imported
            </MenuItem>
            <MenuItem
              id={SOURCE_SPARK_KEY}
              textValue="Spark"
              className={styles.item()}
              isDisabled={disabledSources.includes('spark') && source !== 'spark'}
            >
              Spark
            </MenuItem>
          </MenuSection>
          {showClassFilter && (
            <MenuSection className={styles.section({ withDivider: true })}>
              <Header className={styles.header()}>CLASS</Header>
              <MenuItem id={CLASS_ALL_KEY} textValue="All classes" className={styles.item()}>
                All classes
              </MenuItem>
              {classes.map((assetClassOption) => (
                <MenuItem
                  key={assetClassOption}
                  id={`${CLASS_KEY_PREFIX}${assetClassOption}`}
                  textValue={assetClassOption}
                  className={styles.item()}
                  isDisabled={disabledClasses.includes(assetClassOption) && assetClassOption !== assetClass}
                >
                  {assetClassOption}
                </MenuItem>
              ))}
            </MenuSection>
          )}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}

export { ASSETS_FILTER_SOURCES, AssetsFilterMenu };
export type { AssetsFilterMenuProps, AssetsFilterSource };
